import React from 'react';
import styled from 'styled-components';
import {useLocation, useMatch} from 'react-router-dom';
import {
    getHelmetForMetaTags,
    getInitialCmsPageSharedValues,
    getUrlPattern,
    useCmsData
} from 'epic-ue-shared';
import LoadIndicator from '../components/common/loading/LoadIndicator';
import {
    CmsComponentMapper,
    DynamicSections,
    ImageHero,
    ImageHeroProps,
    ImmersiveHeaderSection,
    Subnav,
    SubnavProps,
    TemplateThemeProps
} from 'epic-ue-ui';
import {UeThemeProvider, ueLightTheme, ueDarkTheme, useSetCurrentAccentColor} from 'epic-ue-theme';
import {
    GeneralLandingHeaderProps,
    DynamicHeaderProps,
    isDynamicHeaderType,
    GeneralLandingPageData,
    GeneralLandingPageSections
} from 'epic-ue-ui';
import Error404 from './error/Error404';

const Styled = styled.div`
    position: relative;
    overflow: hidden;
`;

interface Properties {
    cmsUrlPattern?: string;
}

const GeneralLandingPage = ({cmsUrlPattern = ''}: Properties): JSX.Element => {
    const {pathname} = useLocation();
    const match = useMatch(pathname);
    const urlPattern = cmsUrlPattern || getUrlPattern(match);
    const {cmsLoading, cmsLoadedEmpty, pageData} = useCmsData<GeneralLandingPageData>(urlPattern);

    const {
        header = {} as GeneralLandingHeaderProps,
        subnav = {} as SubnavProps,
        themeConfig = {} as TemplateThemeProps,
        sections = {} as GeneralLandingPageSections
    } = pageData;

    const isDynamicHeader = isDynamicHeaderType(header);
    const {headerOptions} = isDynamicHeader ? (header as DynamicHeaderProps) : {headerOptions: []};
    const {items = []} = sections;
    const {accentColor, theme} = themeConfig;
    useSetCurrentAccentColor(accentColor);

    if (cmsLoading) {
        return (
            <UeThemeProvider theme={theme === 'light' ? ueLightTheme : ueDarkTheme}>
                <Styled>
                    <LoadIndicator key="careers-overview-loading" />
                </Styled>
            </UeThemeProvider>
        );
    }

    if (cmsLoadedEmpty) {
        return <Error404 />;
    }

    return (
        <UeThemeProvider theme={theme === 'light' ? ueLightTheme : ueDarkTheme}>
            <Styled className={theme}>
                {getHelmetForMetaTags(pageData)}
                {isDynamicHeader ? (
                    <CmsComponentMapper
                        dynamicPageDataArray={headerOptions}
                        elementMapping={{
                            imageHero: <ImageHero accentColor={accentColor} />,
                            immersiveHeader: <ImmersiveHeaderSection accentColor={accentColor} />
                        }}
                    />
                ) : (
                    <ImageHero {...(header as ImageHeroProps)} accentColor={accentColor} />
                )}
                <Subnav {...subnav} accentColor={accentColor} />
                <DynamicSections theme={theme} items={items} accentColor={accentColor} />
            </Styled>
        </UeThemeProvider>
    );
};

GeneralLandingPage.getInitialSharedValues = getInitialCmsPageSharedValues();

export default GeneralLandingPage;
